import React, { useState } from 'react';
import { X, Share2, Copy, Check, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import PartnerInviteModal from './PartnerInviteModal';
import { copyToClipboard } from '../utils/clipboard';
import { buildPublicShareUrl, CURRENT_ORIGIN_IS_PRIVATE, HAS_PUBLIC_SHARE_ORIGIN } from '../config';

const AlternativeProductModal = ({ listId, listName, items, onClose, onShareGenerated }) => {
    const [alternatives, setAlternatives] = useState({});
    const [shareUrl, setShareUrl] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [copied, setCopied] = useState(false);
    const [showPartnerInvite, setShowPartnerInvite] = useState(false);
    const [shareResponse, setShareResponse] = useState(null);

    const handleAlternativeChange = (itemId, value) => {
        setAlternatives(prev => ({
            ...prev,
            [itemId]: value.substring(0, 100) // Max 100 chars
        }));
    };

    const getWhatsAppMessage = (url) => {
        const template =
            `🛒 *Bana Bir Tarif Ver*
Alışveriş listesi senin için hazır şefim! 👨‍🍳

📦 *Liste:* ${listName || 'Alışveriş Listesi'}

---------------------------
👇 *Listeyi açmak için tıkla:*
${url}
---------------------------

_Bu link 24 saat geçerlidir._`;
        return encodeURIComponent(template);
    };

    const handleGenerateShare = async () => {
        try {
            setIsGenerating(true);

            // First, update items with alternatives
            const itemsToUpdate = items.map(item => ({
                id: item.id,
                ingredientName: item.ingredientName,
                brand: item.brand,
                suggestedAmount: item.suggestedAmount,
                unit: item.unit,
                alternativeProduct: alternatives[item.id] || null
            }));

            const authToken = localStorage.getItem('authToken');
            const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

            // Update items with alternatives
            await axios.patch(`/api/shopping/lists/${listId}/items`,
                { items: itemsToUpdate },
                { headers }
            );

            // Generate share token
            const response = await axios.post(`/api/shopping/lists/${listId}/share`,
                {},
                { headers }
            );

            setShareResponse(response.data);

            const fullUrl = buildPublicShareUrl(response.data.shareLink);
            setShareUrl(fullUrl);
            if (CURRENT_ORIGIN_IS_PRIVATE && !HAS_PUBLIC_SHARE_ORIGIN) {
                toast.error('Link yerel ağ adresiyle üretildiği için WhatsApp önizlemesi görünmeyebilir.');
            }

            toast.success('Paylaşım linki oluşturuldu!');

            // --- AUTOMATIC SHARE TRIGGER ---
            window.open(`https://wa.me/?text=${getWhatsAppMessage(fullUrl)}`, '_blank');
            // -------------------------------

            // --- AUTO CLOSE OR INVITE ---
            if (response.data.shouldPromptPartner) {
                console.log('🚀 Triggering Partner Invite Modal...');
                setTimeout(() => {
                    setShowPartnerInvite(true);
                }, 1000);
            } else {
                // Only auto-close if we are not showing an invite
                setTimeout(() => {
                    onClose();
                }, 10000);
            }
            // -----------------------------------
        } catch (error) {
            console.error('Error generating share link:', error);
            toast.error('Paylaşım linki oluşturulamadı');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleCopyLink = async () => {
        if (shareUrl) {
            const success = await copyToClipboard(shareUrl);
            if (success) {
                setCopied(true);
                toast.success('Link kopyalandı!');
                setTimeout(() => setCopied(false), 2000);
            } else {
                toast.error('Kopyalanamadı, lütfen manuel kopyalayın.');
            }
        }
    };

    const handleWhatsAppShare = () => {
        if (shareUrl) {
            window.open(`https://wa.me/?text=${getWhatsAppMessage(shareUrl)}`, '_blank');
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
            <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden border border-white/20 dark:border-slate-800">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 flex justify-between items-center shrink-0">
                    <div className="flex items-center gap-3">
                        <Share2 size={24} />
                        <h2 className="text-xl font-black">Listeyi Paylaş</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-white hover:bg-white hover:bg-opacity-20 rounded-xl p-2 transition"
                    >
                        <X size={24} />
                    </button>
                </div>

                {!shareUrl ? (
                    <>
                        {/* Info Banner */}
                        <div className="bg-blue-50/50 dark:bg-blue-900/10 border-l-4 border-blue-400 p-4 m-6 mb-4 rounded-r-xl">
                            <div className="flex items-start gap-3">
                                <AlertCircle className="text-blue-500 flex-shrink-0 mt-0.5" size={20} />
                                <div className="text-sm text-blue-800 dark:text-blue-200">
                                    <p className="font-bold mb-1">İkame Ürün Önerisi (Opsiyonel)</p>
                                    <p className="opacity-80">Ürün bulunamazsa ne alınabileceğini belirtebilirsiniz. Örnek: "Yörükoğlu veya Pınar"</p>
                                </div>
                            </div>
                        </div>

                        {/* Items List */}
                        <div className="p-6 pt-2 overflow-y-auto max-h-[50vh] no-scrollbar">
                            <div className="space-y-4">
                                {items.map((item, idx) => (
                                    <div key={item.id || `alt-${idx}`} className="bg-gray-50 dark:bg-slate-800/50 border border-gray-100 dark:border-slate-800 rounded-2xl p-4 hover:shadow-md transition group">
                                        <div className="flex justify-between items-start mb-2">
                                            <div>
                                                <h3 className="font-bold text-gray-800 dark:text-gray-200 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                                    {item.ingredientName}
                                                </h3>
                                                {item.brand && (
                                                    <p className="text-xs text-gray-400 font-bold uppercase tracking-tighter mt-0.5">Marka: {item.brand}</p>
                                                )}
                                                <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                                                    {item.suggestedAmount} {item.unit}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="mt-3">
                                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 ml-1">
                                                🔄 Yoksa ne alayım?
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Örn: Yörükoğlu veya Pınar"
                                                maxLength={100}
                                                value={alternatives[item.id] || ''}
                                                onChange={(e) => handleAlternativeChange(item.id, e.target.value)}
                                                className="w-full px-4 py-2.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="border-t border-gray-100 dark:border-slate-800 p-6 bg-gray-50/50 dark:bg-slate-900/50 flex justify-between items-center shrink-0">
                            <button
                                onClick={onClose}
                                className="px-6 py-2 text-gray-500 dark:text-gray-400 font-bold hover:text-gray-800 dark:hover:text-white transition"
                            >
                                Vazgeç
                            </button>
                            <button
                                onClick={handleGenerateShare}
                                disabled={isGenerating}
                                className="px-8 py-3 bg-blue-600 text-white rounded-2xl hover:bg-blue-700 shadow-xl shadow-blue-600/20 transition-all font-black uppercase tracking-widest text-xs flex items-center gap-2 disabled:opacity-50"
                            >
                                {isGenerating ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" />
                                        Hazırlanıyor...
                                    </>
                                ) : (
                                    <>
                                        <Share2 size={18} />
                                        Hemen Paylaş
                                    </>
                                )}
                            </button>
                        </div>
                    </>
                ) : (
                    /* Share Link Display */
                    <div className="p-6">
                        <div className="text-center mb-6">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Check className="text-green-500" size={32} />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">
                                Paylaşım Linki Hazır!
                            </h3>
                            <p className="text-gray-600">
                                Link 24 saat geçerlidir
                            </p>
                        </div>

                        <div className="bg-gray-100 rounded-lg p-4 mb-4">
                            <p className="text-sm text-gray-600 mb-2">Paylaşım Linki:</p>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={shareUrl}
                                    readOnly
                                    className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm"
                                />
                                <button
                                    onClick={handleCopyLink}
                                    className={`px-4 py-2 rounded-lg transition flex items-center gap-2 ${copied
                                        ? 'bg-green-500 text-white'
                                        : 'bg-blue-500 text-white hover:bg-blue-600'
                                        }`}
                                >
                                    {copied ? (
                                        <>
                                            <Check size={18} />
                                            Kopyalandı
                                        </>
                                    ) : (
                                        <>
                                            <Copy size={18} />
                                            Kopyala
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3">
                            <button
                                onClick={handleWhatsAppShare}
                                className="flex-1 px-6 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition flex items-center justify-center gap-2 font-bold uppercase tracking-widest text-xs shadow-lg shadow-green-500/20"
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                                </svg>
                                WhatsApp'ta Paylaş
                            </button>
                            <button
                                onClick={onClose}
                                className="px-6 py-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition font-bold uppercase tracking-widest text-xs"
                            >
                                Kapat
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Partner Invite Modal */}
            <PartnerInviteModal
                isOpen={showPartnerInvite}
                onClose={() => setShowPartnerInvite(false)}
                onInviteSent={() => {
                    setShowPartnerInvite(false);
                    toast.success('Partner daveti gönderildi!');
                }}
            />
        </div>
    );
};

export default AlternativeProductModal;
